import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Wrench, Clock, AlertCircle, CheckCircle, Package, ShoppingCart, Building, Plus, Upload, X, Camera, Image, Edit2, Trash2 } from "lucide-react";
import PurchasingTab from "./tabs/PurchasingTab";
import InventoryTab from "./tabs/InventoryTab";
import SuppliersTab from "./tabs/SuppliersTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date as dd/mm/yyyy
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'pending': return <Clock className="w-4 h-4" />;
    case 'in_progress': return <AlertCircle className="w-4 h-4" />;
    case 'completed': return <CheckCircle className="w-4 h-4" />;
    default: return null;
  }
};

export default function MechanicDashboard({ user, onLogout }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("repairs");
  const [statusFilter, setStatusFilter] = useState("all");
  const [addingItemToRepair, setAddingItemToRepair] = useState(null);
  const [itemToAdd, setItemToAdd] = useState({ item_id: "", quantity: 1, itemSearch: "" });
  const [showCreateRepair, setShowCreateRepair] = useState(false);
  const [repairTypes, setRepairTypes] = useState([]);
  const [newRepairType, setNewRepairType] = useState("");
  const [showAddType, setShowAddType] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editTypeName, setEditTypeName] = useState("");
  const [newRepair, setNewRepair] = useState({
    repair_type: "",
    vehicle_number: "",
    damage_photo: "",
    vehicle_photo: ""
  });
  const damagePhotoRef = useRef(null);
  const vehiclePhotoRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, repairsRes, inventoryRes, lowStockRes, purchasesRes, suppliersRes, mechanicsRes, repairTypesRes] = await Promise.all([
        axios.get(`${API}/mechanic/dashboard`),
        axios.get(`${API}/repairs`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/low-stock`),
        axios.get(`${API}/purchases`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/mechanics`),
        axios.get(`${API}/repair-types`)
      ]);
      setDashboardData(dashboardRes.data);
      setRepairs(repairsRes.data || []);
      setInventory(inventoryRes.data || []);
      setLowStockItems(lowStockRes.data || []);
      setPurchases(purchasesRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setMechanics(mechanicsRes.data || []);
      setRepairTypes(repairTypesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (repairId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/repairs/${repairId}`, { status: newStatus });
      toast.success("Status updated!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleClaim = async (repairId) => {
    try {
      await axios.post(`${API}/repairs/${repairId}/claim`);
      toast.success("Repair claimed — assigned to you");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim repair");
    }
  };

  const [photoViewer, setPhotoViewer] = useState(null); // { repair, damage_photo, vehicle_photo }
  const handleViewPhotos = async (repair) => {
    try {
      const r = await axios.get(`${API}/repairs/${repair.id}/photos`);
      setPhotoViewer({ repair, ...r.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load photos");
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!itemToAdd.item_id) {
      toast.error("Please select an item");
      return;
    }
    try {
      await axios.post(`${API}/admin/repairs/${addingItemToRepair.id}/add-item`, {
        item_id: itemToAdd.item_id,
        quantity: parseInt(itemToAdd.quantity)
      });
      toast.success("Item added to repair!");
      setAddingItemToRepair(null);
      setItemToAdd({ item_id: "", quantity: 1, itemSearch: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    }
  };

  const handleCreateRepair = async (e) => {
    e.preventDefault();
    if (!newRepair.repair_type) {
      toast.error("Please select a repair type");
      return;
    }
    try {
      await axios.post(`${API}/admin/repairs`, {
        repair_type: newRepair.repair_type,
        mechanic_id: user.id, // Assign to self
        vehicle_number: newRepair.vehicle_number,
        damage_photo: newRepair.damage_photo,
        vehicle_photo: newRepair.vehicle_photo
      });
      toast.success("Repair created successfully!");
      setShowCreateRepair(false);
      setNewRepair({
        repair_type: "",
        vehicle_number: "",
        damage_photo: "",
        vehicle_photo: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create repair");
    }
  };

  const handleAddRepairType = async () => {
    if (!newRepairType.trim()) {
      toast.error("Please enter a repair type name");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/repair-types`, { name: newRepairType });
      setRepairTypes([...repairTypes, res.data]);
      setNewRepair({ ...newRepair, repair_type: res.data.name });
      setNewRepairType("");
      setShowAddType(false);
      toast.success("Repair type added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add repair type");
    }
  };

  const handleEditRepairType = async () => {
    if (!editTypeName.trim()) {
      toast.error("Please enter a repair type name");
      return;
    }
    try {
      await axios.patch(`${API}/admin/repair-types/${editingType.id}`, { name: editTypeName });
      setRepairTypes(repairTypes.map(t => t.id === editingType.id ? { ...t, name: editTypeName } : t));
      // Update selected repair type if it was the edited one
      if (newRepair.repair_type === editingType.name) {
        setNewRepair({ ...newRepair, repair_type: editTypeName });
      }
      setEditingType(null);
      setEditTypeName("");
      toast.success("Repair type updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update repair type");
    }
  };

  const handleDeleteRepairType = async (typeId, typeName) => {
    if (!window.confirm(`Are you sure you want to delete "${typeName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/repair-types/${typeId}`);
      setRepairTypes(repairTypes.filter(t => t.id !== typeId));
      // Clear selection if deleted type was selected
      if (newRepair.repair_type === typeName) {
        setNewRepair({ ...newRepair, repair_type: "" });
      }
      toast.success("Repair type deleted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete repair type");
    }
  };

  const handlePhotoUpload = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setNewRepair({ ...newRepair, [field]: event.target.result });
    };
    reader.readAsDataURL(file);
  };

  const [ownershipFilter, setOwnershipFilter] = useState("all"); // "all" | "mine" | "unassigned"

  const filteredRepairs = repairs.filter(r => {
    const statusOk = statusFilter === "all" || r.status === statusFilter;
    const ownershipOk =
      ownershipFilter === "all" ||
      (ownershipFilter === "mine" && r.mechanic_id === user.id) ||
      (ownershipFilter === "unassigned" && !r.mechanic_id);
    return statusOk && ownershipOk;
  });

  const filteredItems = inventory.filter(i =>
    i.name?.toLowerCase().includes(itemToAdd.itemSearch.toLowerCase()) && i.quantity > 0
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const tabs = [
    { id: "repairs", label: "Repairs", icon: Wrench, color: "orange" },
    { id: "purchasing", label: "Purchasing", icon: ShoppingCart, color: "green" },
    { id: "inventory", label: "Inventory", icon: Package, color: "blue" },
    { id: "suppliers", label: "Suppliers", icon: Building, color: "teal" },
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-white/10 px-4 py-3 sm:p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-white leading-tight truncate">MECHANIC DASHBOARD</h1>
              <p className="text-white/60 text-xs sm:text-sm truncate">{user?.name || user?.full_name}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 p-2 sm:px-4 sm:py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex-shrink-0"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-zinc-900 border border-yellow-500/20 rounded-xl p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <Clock className="w-5 h-5 sm:w-8 sm:h-8 text-yellow-400" />
              <div>
                <p className="text-yellow-400 text-xl sm:text-2xl font-bold">{dashboardData?.pending_repairs || 0}</p>
                <p className="text-white/60 text-[11px] sm:text-sm leading-tight">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-blue-500/20 rounded-xl p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-blue-400" />
              <div>
                <p className="text-blue-400 text-xl sm:text-2xl font-bold">{dashboardData?.in_progress_repairs || 0}</p>
                <p className="text-white/60 text-[11px] sm:text-sm leading-tight">In Progress</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-green-500/20 rounded-xl p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <CheckCircle className="w-5 h-5 sm:w-8 sm:h-8 text-green-400" />
              <div>
                <p className="text-green-400 text-xl sm:text-2xl font-bold">{dashboardData?.completed_repairs || 0}</p>
                <p className="text-white/60 text-[11px] sm:text-sm leading-tight">Completed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const colorClasses = {
              orange: isActive ? "bg-orange-500 text-white" : "bg-zinc-800 text-white/60 hover:bg-zinc-700",
              green: isActive ? "bg-green-500 text-white" : "bg-zinc-800 text-white/60 hover:bg-zinc-700",
              blue: isActive ? "bg-blue-500 text-white" : "bg-zinc-800 text-white/60 hover:bg-zinc-700",
              teal: isActive ? "bg-teal-500 text-white" : "bg-zinc-800 text-white/60 hover:bg-zinc-700",
            };
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${colorClasses[tab.color]}`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "repairs" && (
          <div className="space-y-4">
            {/* Header with Create Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-bold text-white">All Repairs</h2>
              <button
                onClick={() => setShowCreateRepair(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                data-testid="create-repair-btn"
              >
                <Plus className="w-4 h-4" />
                Create Repair
              </button>
            </div>

            {/* Filters */}
            <div className="space-y-2">
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: 'all', label: 'All Repairs' },
                  { key: 'mine', label: 'Mine' },
                  { key: 'unassigned', label: 'Unassigned' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setOwnershipFilter(opt.key)}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                      ownershipFilter === opt.key
                        ? 'bg-orange-500 text-white'
                        : 'bg-zinc-800 text-white/60 hover:bg-zinc-700'
                    }`}
                    data-testid={`ownership-filter-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'pending', 'in_progress', 'completed'].map(status => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                      statusFilter === status
                        ? status === 'all' ? 'bg-orange-500 text-white' : getStatusColor(status)
                        : 'bg-zinc-800 text-white/60 hover:bg-zinc-700'
                    }`}
                    data-testid={`filter-${status}`}
                  >
                    {status === 'all' ? 'All Status' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            {/* Repairs List */}
            <div className="space-y-4">
              {filteredRepairs.map((repair) => (
                <div key={repair.id} className="bg-zinc-900 rounded-xl p-4 border border-white/10">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-white">{repair.title}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full border flex items-center gap-1 ${getStatusColor(repair.status)}`}>
                          {getStatusIcon(repair.status)}
                          {repair.status.replace('_', ' ')}
                        </span>
                        {repair.booking_source === 'driver' && (
                          <span className="px-2 py-0.5 text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/40 uppercase font-bold rounded">
                            Driver Booking
                          </span>
                        )}
                        {repair.priority && (
                          <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${
                            repair.priority === 'high' ? 'bg-red-500/20 text-red-300 border-red-500/40'
                            : repair.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40'
                            : 'bg-green-500/20 text-green-300 border-green-500/40'
                          }`}>
                            {repair.priority}
                          </span>
                        )}
                        {!repair.mechanic_id && (
                          <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">
                            Unassigned
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-white/40">Vehicle:</span>
                          <p className="text-white">{repair.vehicle_number || "-"}</p>
                        </div>
                        <div>
                          <span className="text-white/40">Date{repair.time_slot ? ' / Slot' : ''}:</span>
                          <p className="text-white">{formatDateDMY(repair.assigned_date)}{repair.time_slot ? ` · ${repair.time_slot}` : ''}</p>
                        </div>
                        <div>
                          <span className="text-white/40">Parts Cost:</span>
                          <p className="text-green-400 font-semibold">${repair.total_parts_cost?.toFixed(2)}</p>
                        </div>
                        <div>
                          <span className="text-white/40">Assigned To:</span>
                          <p className={repair.mechanic_id === user.id ? "text-orange-300 font-semibold" : repair.mechanic_name ? "text-white" : "text-yellow-300 italic"}>
                            {repair.mechanic_id === user.id ? "You" : (repair.mechanic_name || "Unassigned")}
                          </p>
                        </div>
                        {repair.driver_name && (
                          <div>
                            <span className="text-white/40">Driver:</span>
                            <p className="text-white">{repair.driver_name}</p>
                          </div>
                        )}
                      </div>
                      {repair.issue_description && (
                        <p className="text-white/70 text-sm mt-2"><span className="text-white/40">Issue: </span>{repair.issue_description}</p>
                      )}
                      {repair.description && (
                        <p className="text-white/60 text-sm mt-2">{repair.description}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:flex sm:flex-col gap-2 justify-end">
                      {!repair.mechanic_id && (
                        <button
                          onClick={() => handleClaim(repair.id)}
                          className="col-span-2 flex items-center justify-center gap-2 px-3 py-2 bg-green-500/20 text-green-400 border border-green-500/40 rounded-lg hover:bg-green-500/30 transition-colors font-semibold whitespace-nowrap"
                          title="Assign this repair to yourself"
                          data-testid={`claim-repair-${repair.id}`}
                        >
                          <Wrench className="w-4 h-4" />
                          Claim
                        </button>
                      )}
                      <button
                        onClick={() => handleViewPhotos(repair)}
                        className={`${repair.mechanic_id && repair.mechanic_id !== user.id ? 'col-span-2' : ''} flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded-lg hover:bg-purple-500/30 transition-colors whitespace-nowrap`}
                        title="View damage & vehicle photos"
                        data-testid={`view-photos-${repair.id}`}
                      >
                        <Image className="w-4 h-4" />
                        Photos
                      </button>
                      {repair.mechanic_id === user.id && (
                        <>
                          <select
                            value={repair.status}
                            onChange={(e) => handleStatusChange(repair.id, e.target.value)}
                            className="px-2 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-w-0"
                            data-testid={`repair-status-${repair.id}`}
                          >
                            <option value="scheduled">Scheduled</option>
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                          <button
                            onClick={() => setAddingItemToRepair(repair)}
                            className="col-span-2 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors whitespace-nowrap"
                            title="Add parts"
                            data-testid={`add-parts-${repair.id}`}
                          >
                            <Package className="w-4 h-4" />
                            Add Parts
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Items Used */}
                  {repair.items_used && repair.items_used.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <h4 className="text-sm font-medium text-white/60 mb-2">Parts Used:</h4>
                      <div className="flex flex-wrap gap-2">
                        {repair.items_used.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-zinc-800 rounded-full text-sm">
                            <span className="text-white">{item.item_name}</span>
                            <span className="text-white/60">x{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filteredRepairs.length === 0 && (
                <div className="bg-zinc-900 rounded-xl p-8 text-center text-white/40">
                  No repairs found
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "purchasing" && (
          <PurchasingTab
            purchases={purchases}
            mechanics={mechanics}
            inventory={inventory}
            suppliers={suppliers}
            fetchPurchases={fetchData}
            user={user}
          />
        )}

        {activeTab === "inventory" && (
          <InventoryTab
            inventory={inventory}
            lowStockItems={lowStockItems}
            fetchInventory={fetchData}
            user={user}
          />
        )}

        {activeTab === "suppliers" && (
          <SuppliersTab
            suppliers={suppliers}
            fetchSuppliers={fetchData}
            user={user}
          />
        )}
      </div>

      {/* Add Item Modal */}
      {addingItemToRepair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-md border border-white/10">
            <h3 className="text-xl font-bold text-white mb-2">Add Parts to Repair</h3>
            <p className="text-white/60 text-sm mb-4">{addingItemToRepair.title}</p>
            <form onSubmit={handleAddItem} className="space-y-4">
              <div className="relative">
                <label className="block text-white/60 text-sm mb-1">Select Item *</label>
                <input
                  type="text"
                  value={itemToAdd.itemSearch}
                  onChange={(e) => setItemToAdd({ ...itemToAdd, itemSearch: e.target.value, item_id: "" })}
                  placeholder="Search item..."
                  className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="add-item-search"
                />
                {itemToAdd.itemSearch && !itemToAdd.item_id && filteredItems.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredItems.map(i => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => setItemToAdd({
                          ...itemToAdd,
                          item_id: i.id,
                          itemSearch: `${i.name} (${i.quantity} available)`
                        })}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10"
                      >
                        {i.name} ({i.quantity} available)
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Quantity *</label>
                <input
                  type="number"
                  value={itemToAdd.quantity}
                  onChange={(e) => setItemToAdd({ ...itemToAdd, quantity: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                  data-testid="add-item-quantity"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setAddingItemToRepair(null); setItemToAdd({ item_id: "", quantity: 1, itemSearch: "" }); }}
                  className="flex-1 px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  data-testid="submit-add-item"
                >
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Repair Modal */}
      {showCreateRepair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Create Repair</h3>
            <form onSubmit={handleCreateRepair} className="space-y-4">
              
              {/* Repair Type Selection */}
              <div>
                <label className="block text-white/60 text-sm mb-2">Repair Type *</label>
                <div className="space-y-2 max-h-56 overflow-y-auto p-2 bg-zinc-800/50 border border-white/10 rounded-lg">
                  {repairTypes.map(type => (
                    <div key={type.id} className="flex items-center gap-2">
                      {editingType?.id === type.id ? (
                        <div className="flex-1 flex gap-2">
                          <input
                            type="text"
                            value={editTypeName}
                            onChange={(e) => setEditTypeName(e.target.value)}
                            className="flex-1 px-3 py-2 bg-zinc-700 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={handleEditRepairType}
                            className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingType(null); setEditTypeName(""); }}
                            className="px-2 py-1 bg-zinc-600 text-white rounded text-sm hover:bg-zinc-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setNewRepair({ ...newRepair, repair_type: type.name })}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                              newRepair.repair_type === type.name
                                ? "bg-orange-500 text-white"
                                : "bg-zinc-700 text-white/80 hover:bg-zinc-600"
                            }`}
                            data-testid={`repair-type-${type.id}`}
                          >
                            {type.name}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingType(type); setEditTypeName(type.name); }}
                            className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRepairType(type.id, type.name)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Add new repair type */}
                {showAddType ? (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={newRepairType}
                      onChange={(e) => setNewRepairType(e.target.value)}
                      placeholder="New repair type..."
                      className="flex-1 px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="new-repair-type-input"
                    />
                    <button
                      type="button"
                      onClick={handleAddRepairType}
                      className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddType(false); setNewRepairType(""); }}
                      className="px-3 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 text-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAddType(true)}
                    className="mt-2 text-sm text-orange-400 hover:text-orange-300 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add new repair type
                  </button>
                )}
              </div>

              {/* Vehicle Number */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Vehicle Number</label>
                <input
                  type="text"
                  value={newRepair.vehicle_number}
                  onChange={(e) => setNewRepair({ ...newRepair, vehicle_number: e.target.value })}
                  placeholder="e.g., SGX-1234A"
                  className="w-full px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-repair-vehicle"
                />
              </div>

              {/* Damage Photo */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Photo of Damage</label>
                <input
                  type="file"
                  ref={damagePhotoRef}
                  onChange={(e) => handlePhotoUpload(e, 'damage_photo')}
                  accept="image/*"
                  className="hidden"
                />
                {newRepair.damage_photo ? (
                  <div className="relative">
                    <img
                      src={newRepair.damage_photo}
                      alt="Damage"
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewRepair({ ...newRepair, damage_photo: "" })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => damagePhotoRef.current?.click()}
                    className="w-full p-4 border-2 border-dashed border-white/20 rounded-lg text-white/60 hover:border-orange-500 hover:text-orange-400 transition-colors flex items-center justify-center gap-2"
                    data-testid="upload-damage-photo"
                  >
                    <Camera className="w-5 h-5" />
                    Upload Damage Photo
                  </button>
                )}
              </div>

              {/* Vehicle Photo */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Vehicle Photo</label>
                <input
                  type="file"
                  ref={vehiclePhotoRef}
                  onChange={(e) => handlePhotoUpload(e, 'vehicle_photo')}
                  accept="image/*"
                  className="hidden"
                />
                {newRepair.vehicle_photo ? (
                  <div className="relative">
                    <img
                      src={newRepair.vehicle_photo}
                      alt="Vehicle"
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewRepair({ ...newRepair, vehicle_photo: "" })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => vehiclePhotoRef.current?.click()}
                    className="w-full p-4 border-2 border-dashed border-white/20 rounded-lg text-white/60 hover:border-orange-500 hover:text-orange-400 transition-colors flex items-center justify-center gap-2"
                    data-testid="upload-vehicle-photo"
                  >
                    <Image className="w-5 h-5" />
                    Upload Vehicle Photo
                  </button>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateRepair(false);
                    setNewRepair({
                      repair_type: "",
                      vehicle_number: "",
                      damage_photo: "",
                      vehicle_photo: ""
                    });
                    setShowAddType(false);
                    setNewRepairType("");
                  }}
                  className="flex-1 px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  data-testid="submit-create-repair"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo viewer modal */}
      {photoViewer && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="photo-viewer-modal" onClick={() => setPhotoViewer(null)}>
          <div className="bg-zinc-900 border border-white/10 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-white/10 sticky top-0 bg-zinc-900 z-10">
              <div>
                <h3 className="text-lg font-semibold text-white">{photoViewer.repair.title}</h3>
                <p className="text-xs text-white/60">
                  {photoViewer.repair.driver_name && <>Driver: {photoViewer.repair.driver_name} · </>}
                  Vehicle: {photoViewer.repair.vehicle_number}
                </p>
                {photoViewer.repair.issue_description && (
                  <p className="text-sm text-white/80 mt-2 whitespace-pre-wrap"><span className="text-white/40">Issue:</span> {photoViewer.repair.issue_description}</p>
                )}
              </div>
              <button onClick={() => setPhotoViewer(null)} className="text-white/40 hover:text-white p-1" data-testid="close-photo-viewer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase font-bold text-white/60 mb-2">Damage Photo</p>
                {photoViewer.damage_photo ? (
                  <img src={photoViewer.damage_photo} alt="Damage" className="w-full border border-white/10 rounded" />
                ) : (
                  <div className="w-full h-40 border border-dashed border-white/10 flex items-center justify-center text-white/40 text-sm">No damage photo</div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase font-bold text-white/60 mb-2">Vehicle Photo</p>
                {photoViewer.vehicle_photo ? (
                  <img src={photoViewer.vehicle_photo} alt="Vehicle" className="w-full border border-white/10 rounded" />
                ) : (
                  <div className="w-full h-40 border border-dashed border-white/10 flex items-center justify-center text-white/40 text-sm">No vehicle photo</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
